import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import FormulaModal from './FormulaModal';

const plans = [
  {
    name: 'Essentielle',
    price: '350 000 XOF',
    tagline: 'À partir de',
    highlight: false,
    items: [
      "Cartes d'invitation digitales",
      'Photocall/Backdrop + ballons',
      '1 stand goûter (barbe à papa, crêpes, popcorn…)',
      'Paquets cadeaux invités',
    ],
    icon: 'ri-gift-line',
  },
  {
    name: 'Signature',
    price: '550 000 XOF',
    tagline: 'À partir de',
    highlight: true,
    items: [
      "Cartes d'invitation digitales",
      'Photocall/Backdrop + ballons',
      'Candy bar',
      'Mise en place (logistique &amp; déco)',
      'Paquets cadeaux invités',
    ],
    icon: 'ri-star-line',
  },
  {
    name: 'Magique',
    price: '850 000 XOF',
    tagline: 'À partir de',
    highlight: false,
    items: [
      "Cartes d'invitation digitales",
      'Décoration entrée',
      'Photocall/Backdrop + ballons',
      'Candy bar',
      'Mise en place complète',
      'Paquets cadeaux invités',
      '1 animateur (jeux inclus)',
      'Atelier maquillage',
    ],
    icon: 'ri-magic-line',
  },
];

type Plan = typeof plans[number];

const PricingCards = () => {
  const { ref, isVisible } = useScrollAnimation();
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);

  return (
    <section className="bg-white py-24 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 mb-4">
            <span className="h-px w-6 bg-brand-coral" />
            <span className="text-brand-coral text-xs font-medium tracking-[0.2em] uppercase">Nos Formules</span>
            <span className="h-px w-6 bg-brand-coral" />
          </div>
          <h2 className="font-serif text-4xl lg:text-5xl text-brand-anthracite mb-4">Anniversaires Enfants</h2>
          <p className="text-brand-gray max-w-xl mx-auto text-base leading-relaxed">
            Des formules pensées pour créer des moments magiques, personnalisables selon vos envies.
          </p>
        </div>

        {/* Cards */}
        <div ref={ref} className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
          {plans.map((plan, idx) => (
            <div
              key={plan.name}
              className={`relative rounded-2xl overflow-hidden transition-all duration-600 flex flex-col ${
                plan.highlight
                  ? 'ring-2 ring-brand-fuchsia scale-[1.03]'
                  : 'border border-brand-gold/20'
              } ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${idx * 150}ms`, transitionDuration: '600ms' }}
            >
              {plan.highlight && (
                <div className="absolute top-0 left-0 right-0 h-1" style={{ background: 'linear-gradient(135deg, #e63e8b, #f78b49)' }} />
              )}
              {plan.highlight && (
                <div
                  className="absolute top-4 right-4 px-3 py-1 rounded-full text-white text-xs font-medium"
                  style={{ background: 'linear-gradient(135deg, #e63e8b, #f78b49)' }}
                >
                  Populaire
                </div>
              )}

              {/* Header */}
              <div className="p-8 pb-6 bg-brand-beige">
                <div className={`w-12 h-12 flex items-center justify-center rounded-full mb-4 ${plan.highlight ? 'bg-brand-fuchsia' : 'bg-brand-coral/20'}`}>
                  <i className={`${plan.icon} text-xl ${plan.highlight ? 'text-white' : 'text-brand-coral'}`} />
                </div>
                <h3 className="font-serif text-2xl font-bold text-brand-anthracite mb-1">{plan.name}</h3>
                <p className="text-brand-gray text-xs mb-4">{plan.tagline}</p>
                <div className="flex items-end gap-1">
                  <span className="font-serif text-3xl font-bold text-brand-anthracite">{plan.price}</span>
                </div>
              </div>

              <div className="h-px mx-8 bg-brand-gold/25" />

              {/* Features */}
              <div className="p-8 pt-6 flex flex-col flex-1">
                <ul className="space-y-3 mb-8 flex-1">
                  {plan.items.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-brand-coral text-sm" />
                      </div>
                      <span
                        className="text-sm text-brand-gray leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: item }}
                      />
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <button
                  onClick={() => setSelectedPlan(plan)}
                  className={`w-full py-3 rounded-full text-sm font-medium text-center whitespace-nowrap transition-all duration-300 hover:scale-[1.02] cursor-pointer ${
                    plan.highlight
                      ? 'text-white'
                      : 'border border-brand-fuchsia text-brand-fuchsia hover:bg-brand-fuchsia hover:text-white'
                  }`}
                  style={plan.highlight ? { background: 'linear-gradient(135deg, #e63e8b, #f78b49)' } : {}}
                >
                  Choisir cette formule
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Note */}
        <div className="bg-brand-fuchsia/5 border border-brand-fuchsia/20 rounded-xl p-6 max-w-2xl mx-auto text-center">
          <i className="ri-information-line text-brand-fuchsia text-xl mb-2 block" />
          <p className="text-sm text-brand-gray">
            <strong className="text-brand-anthracite">Formules pour 15–20 personnes.</strong>{' '}
            Toutes nos formules sont personnalisables selon vos préférences, thèmes et contraintes budgétaires.
          </p>
        </div>
      </div>

      {/* Modal */}
      <FormulaModal plan={selectedPlan} onClose={() => setSelectedPlan(null)} />
    </section>
  );
};

export default PricingCards;
